<template>
<div>
    <div v-if="!loading && mediaResults.length" :class="['search-options', {'align-options': !owner && !isMobile}]">
        <select :class="['sort', 'form-control', {'full-width': isMobile}]" v-model="sort" @change="changeFilter">
            <option value="newest" selected="selected">{{ t.newest }}</option>
            <option value="views">{{ t.most_viewed }}</option>
            <option value="likes">{{ t.most_likes }}</option>
            <option value="comments">{{ t.most_comments }}</option>
        </select>
        <div v-if="usePaginator" class="search-num-results form-inline hidden-xs">
            <label for="pagination-show">{{ t.show }}</label>
            <select class="form-control" id="pagination-show" v-model="showPerPage" @change="changeFilter">
                <option :value="28" selected="selected">28</option>
                <option :value="56">56</option>
                <option :value="98">98</option>
            </select>
        </div>
    </div>
    <div class="profile-media">
        <loading v-if="loading"></loading>
        <media v-if="!loading" v-for="media in mediaResults" :key="media.id" :media="media" :stats="currentAlbum.typeId !== 1 && !!media.id"
            :class="[{'mymedia-coverimage': currentAlbum.typeId === 1}, {'mobile-image': isMobile}]" :adultAllowed="media.user ? currentUserId === media.user.id : false"
            @click.native="showMediaViewer(media)">
            <a v-if="!owner" :href="profile.links.profile" class="btn btn-xs btn-dark-ghost dropdown-toggle" v-miniprofile="profile.id" target="_new">
                <i class="fa fa-user"></i>
            </a>
            <div v-if="owner && media.id" class="btn-group pull-right">
                <button type="button" class="btn btn-xs btn-dark-ghost dropdown-toggle" v-dropdown>
                    <span class="caret"></span>
                </button>
                <ul class="dropdown-menu">
                    <li v-if="currentAlbum.typeId !== 1 && media.set === 'default'">
                        <a :href="editPage.replace('0', media.id)" @click.stop>{{ t.edit }}</a>
                    </li>
                    <li><a href="#" @click.stop.prevent="deleteMedia(media)">{{ gt.common.delete }}</a></li>
                    <template v-if="currentAlbum.typeId === 0">
                        <li><a href="#" @click.stop.prevent="showMoveModal(media)">{{ t.move }}</a></li>
                        <li class="divider"></li>
                        <template v-if="!currentAlbum.private">
                            <li v-if="media.unsearchable">
                                <a href="#" @click.stop.prevent="setGalleryOptStatus(media, false)">{{ t.add_to_gallery }}</a>
                            </li>
                            <li v-if="!media.unsearchable">
                                <a href="#" @click.stop.prevent="setGalleryOptStatus(media, true)">{{ t.remove_from_gallery }}</a>
                            </li>
                        </template>
                        <template v-if="media.approved && !media.adult && media.set === 'default'">
                            <li><a href="#" @click.stop.prevent="setAlbumCover(media)">{{ t.set_album_cover }}</a></li>
                            <li v-if="!currentAlbum.private"><a href="#" @click.stop.prevent="setForumAvatar(media)">{{ t.set_forum_avatar }}</a></li>
                            <li v-if="!currentAlbum.private"><a href="#" @click.stop.prevent="setProfilePhoto(media)">{{ t.set_profile_photo }}</a></li>
                        </template>
                    </template>
                    <template v-if="currentAlbum.typeId === 1">
                        <li><a href="#" @click.stop.prevent="setCoverImage(media)">{{ t.set_cover_image }}</a></li>
                    </template>
                </ul>
            </div>
            <div v-if="!media.approved" class="unapproved-overlay">{{ t.awaiting_approval }}</div>
        </media>
        <span v-if="!loading && !mediaResults.length">{{ noResults }}</span>
        <modal v-model="moveModal.show" :title="t.move_media" :submit="t.move" @submit="moveMedia">
            <form class="form-horizontal" @submit="moveMedia">
                <div class="form-group">
                    <label class="col-md-4 control-label" for="name">{{ t.album }}</label>
                    <div class="col-md-4">
                        <select class="form-control" v-model="moveModal.album">
                            <option v-for="album in albums" :key="album.id" v-if="album.typeId === 0" :value="album.id">{{ album.name }}</option>
                        </select>
                    </div>
                </div>
            </form>
        </modal>
    </div>
    <pagination v-if="!loading && totalPages > 1 && usePaginator" @page-changed="setPage" :page="currentPage" :total="totalPages"></pagination>
</div>
</template>

<script>
import ajax from 'Site/js/util/ajax.fetch';
import dropdownDirective from 'Site/directive/dropdown';
import Loading from 'Site/component/Loading.vue';
import Media from 'Media/component/Media.vue';
import miniprofileDirective from 'Profile/directive/miniprofile';
import Modal from 'Site/component/Modal.vue';
import Pagination from 'Site/component/Pagination.vue';
import Sortable from 'sortablejs';
import { mapState } from 'vuex';

export default {
    name: 'ProfileMediaMedia',
    components: {
        Loading,
        Media,
        Modal,
        Pagination
    },
    computed: {
        ...mapState({
            gt: state => state.gt,
            isMobile: state => state.isMobile,
            profile: state => state.config.data.profile,
            t: state => state.t.profile_media,
            currentUserId: state => parseInt(state.siteConfig.user.id, 10),
            user: state => parseInt(state.config.data.userId, 10)
        }),
        formatFetchData() {
            const data = {
                user: this.user,
                album: this.currentAlbum.id,
                sort: this.sort,
                deleted: 0
            };
            if (!this.owner) {
                data.approved = 1;
            }
            if (this.usePaginator) {
                data['max-results'] = this.showPerPage;
                // set page (max-results x (pageNum-1))
                const pageMultiplier = this.currentPage - 1; // offset by -1 so page 2 is x1 multiplier ( 0 x 25 = 0, 1 x 25 = 25)
                data['start-index'] = pageMultiplier * data['max-results'];
            }
            return data;
        },
        noResults() {
            return this.errorAlert ? this.gt.error_alert : this.t.no_results;
        },
        usePaginator() {
            return this.currentAlbum.count > this.maxNoPagination;
        }
    },
    data() {
        return {
            currentPage: 1,
            enableDragSort: false,
            errorAlert: false,
            showPerPage: this.pagination.showPerPage,
            sort: 'newest',
            sortable: {},
            loading: false,
            maxNoPagination: this.pagination.maxNoPagination,
            mediaSetOnMount: false,
            moveModal: {
                album: null,
                media: null,
                show: false
            },
            mediaResults: [],
            totalPages: 1
        };
    },
    directives: {
        dropdown: dropdownDirective,
        miniprofile: miniprofileDirective
    },
    methods: {
        deleteMedia(mediaItem) {
            this.$store.dispatch('showConfirm', {
                text: this.t.delete_confirm,
                callback: () => {
                    ajax.delete('media', { id: mediaItem.id }).then((response) => {
                        this.$store.dispatch('notify', { msg: response.message });
                        this.currentAlbum.count -= 1;
                    });
                    this.mediaResults = this.mediaResults.filter(checkMedia => checkMedia.id !== mediaItem.id);
                }
            });
        },
        enableSort() {
            // check if current album can be dragSorted
            if (!this.owner || !this.enableDragSort || this.isMobile) {
                return;
            }
            let $adding = '';
            // Create sortable for media
            this.sortable = Sortable.create(document.querySelector('.profile-media'), {
                animation: 150,
                group: 'photos',
                onUpdate: (event) => {
                    this.mediaResults.splice(event.newIndex, 0, this.mediaResults.splice(event.oldIndex, 1)[0]);
                    const ids = this.mediaResults.map(collection => collection.id);

                    ajax.create('sort', {
                        ids,
                        album: this.currentAlbum.id
                    }).then((response) => {
                        this.$store.dispatch('notify', { msg: response.message });
                    });
                },
                onMove: (event) => {
                    if (this.currentAlbum.typeId !== 0) {
                        return false;
                    }
                    if (event.to.className === 'album') {
                        if ($adding) {
                            $adding.classList.remove('highlight');
                        }
                        $adding = event.to;
                        event.to.classList.add('highlight');
                    } else if ($adding && event.to.className !== 'album highlight') {
                        $adding.classList.remove('highlight');
                    }
                    return true;
                }
            });

            // Create sortables for albums
            const elements = document.querySelectorAll('.album');
            for (let i = 0; i < elements.length; ++i) {
                const albumElement = elements[i];
                const album = this.albums[i];

                if (album.typeId === 0) {
                    Sortable.create(albumElement, {
                        animation: 150,
                        group: 'photos',
                        sort: false,
                        onAdd: (event) => {
                            const mediaItem = this.mediaResults[event.oldIndex];
                            albumElement.classList.remove('highlight');
                            this.moveMedia(mediaItem, album.id);
                        }
                    });
                }
            }
        },
        showMediaViewer(mediaItem) {
            if (!mediaItem.approved) {
                return;
            }
            if (this.currentAlbum.typeId !== 1) {
                this.mediaResults.forEach((collection) => { collection.user = this.profile; });
                this.$store.dispatch('showMediaViewer', {
                    adultAllowed: this.currentUserId === mediaItem.user.id,
                    media: this.mediaResults,
                    startId: mediaItem.id
                });
            }
        },
        showMoveModal(mediaItem) {
            this.moveModal.show = true;
            this.moveModal.media = mediaItem;
        },
        moveMedia(mediaItem = null, albumId = null) {
            if (!mediaItem) {
                if (!this.moveModal.album) {
                    return;
                }
                mediaItem = this.moveModal.media;
                albumId = this.moveModal.album;
            }

            ajax.update('move', {
                action: 'move',
                album: albumId,
                id: mediaItem.id
            }).then((response) => {
                this.moveModal.show = false;
                this.$store.dispatch('notify', { msg: response.message });
                this.mediaResults = this.mediaResults.filter(collection => collection.id !== mediaItem.id);

                const selectedAlbum = this.albums.filter(albumCheck => albumCheck.id === albumId)[0];
                selectedAlbum.collections.push(mediaItem);

                if (mediaItem.approved) {
                    // Decrement count on album moved from
                    this.currentAlbum.count -= 1;

                    // Increment count on album moved to
                    selectedAlbum.count += 1;
                }
            }).catch(({ response }) => {
                response.then((error) => {
                    this.$store.dispatch('notify', { type: 'error', msg: error.message });
                });
            });
        },
        setAlbumCover(mediaItem) {
            ajax.update('album', {
                id: this.currentAlbum.id,
                cover: mediaItem.id
            }).then((response) => {
                this.$store.dispatch('notify', { msg: response.message });
                this.currentAlbum.cover = mediaItem;
            });
        },
        setCoverImage(mediaItem) {
            ajax.update('cover', {
                id: mediaItem.id,
                user: this.user
            }).then(() => {
                this.$store.dispatch('notify', { msg: this.$store.state.t.cover_image_updated });
                // @todo refactor this once profile is all in Vue
                /* eslint no-underscore-dangle: 0 */
                document.querySelector('#profile-header-vue').__vue__.coverImage =
                    mediaItem.media.mediumcover ? mediaItem.media.mediumcover.url : mediaItem.media.smallcover.url;
            });
        },
        setForumAvatar(mediaItem) {
            ajax.update('forumAvatar', {
                id: this.user,
                avatar: mediaItem.id
            }).then((response) => {
                this.$store.dispatch('notify', { msg: response.message });
            });
        },
        setProfilePhoto(mediaItem) {
            ajax.update('avatar', {
                id: mediaItem.id
            }).then((response) => {
                this.$store.dispatch('notify', { msg: response.message });
                // @todo refactor this once profile is all in Vue
                document.querySelector('#profile-header-vue').__vue__.newAvatar = mediaItem;
            });
        },
        setGalleryOptStatus(mediaItem, status) {
            ajax.update('media', {
                id: mediaItem.id,
                unsearchable: status,
                action: status ? 'removePublicGallery' : 'addPublicGallery'
            }).then((response) => {
                this.$store.dispatch('notify', { msg: response.message });
                mediaItem.unsearchable = status;
            });
        },
        changeFilter() {
            this.currentPage = 1;
            this.fetchData();
        },
        setPage(page) {
            this.currentPage = page;
            this.fetchData();
        },
        fetchData() {
            this.loading = true;
            this.errorAlert = false;

            // get collection
            ajax.get('media', this.formatFetchData).then((response) => {
                this.loading = false;
                this.mediaResults = response.items;
                this.totalPages = Math.ceil(response.count / this.showPerPage);
            }).catch(() => {
                this.loading = false;
                this.mediaResults = [];
                this.errorAlert = true;
            });
        },
    },
    mounted() {
        this.enableSort();
        this.$nextTick(() => {
            this.mediaResults = this.currentAlbum.collections ? this.currentAlbum.collections : [];
            this.totalPages = Math.ceil(this.currentAlbum.count / this.showPerPage);
            this.mediaSetOnMount = true;
        });
    },
    props: {
        albums: {
            type: Array,
            required: true
        },
        currentAlbum: {
            type: Object,
            required: true
        },
        editPage: {
            type: String
        },
        pagination: {
            type: Object
        },
        owner: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        currentAlbum() {
            // wait for intial imprint before watching for changes
            if (this.mediaSetOnMount) {
                this.enableSort();
                this.currentPage = 1;
                this.fetchData();
            }
        }
    }
};

</script>

<style lang="scss">
.search-options {
    height: 32px;
    margin-bottom: 6px;
    .sort {
        max-width: 170px;
        float: right;
        &.full-width {
            max-width: 100%;
        }
    }
    .search-num-results {
        float: right;
        margin-right: 10px;
        margin-bottom: 0;
    }
}
.align-options {
    margin-top: -48px;
}
.profile-media {
    clear: both;
    display: flex;
    flex-flow: row wrap;
    justify-content: space-around;
    .btn-group {
        position: absolute;
        top: 5px;
        right: 5px;
    }
    .mobile-image {
        width: 139px;
        height: 139px;
        margin: 3px;
    }
    .media-item .fa-lock {
        right: auto;
        left: 10px;
        top: 10px;
    }
    .sortable-ghost {
        opacity: .2;
    }
    .unapproved-overlay {
        position: absolute;
        bottom: 47px;
        background-color: rgba(0,0,0,0.5);
        width: 100%;
        padding: 5px;
        text-align: center;
        color: #fff;
        text-shadow: 0 0 3px #000;
    }
    .mymedia-coverimage {
        height: 100px;
        width: 300px;
        .btn-group {
            right: 5px;
            left: auto;
        }
        .unapproved-overlay {
            bottom: 17px;
        }
        .gallery-result-image {
            height: 100px;
        }
    }
}
</style>
